import { PlayerStore } from '@/front/stores/player_store.js';

import { PlaybackStore } from '@/front/stores/playback_store.js';

import { VideoStore } from '@/front/stores/video_store.js';

import { AnnStore } from '@/front/stores/ann_store.js';

import { formatSeconds } from '@/front/composables/TimeFormatter.js';

import { doVariableEvaluation } from '@/front/composables/ClickComposer.js';

// For window postMessage sending and receiving

export function MessageComposer() {

  function emitMessage(str, data) {
    const videoStore = VideoStore();
    const playerStore = PlayerStore();
    const playbackStore = PlaybackStore();
    const annStore = AnnStore();
    let video = videoStore.video;

    let currentScore = annStore.currentScore;

    var obj = {
      event: str,
      version: 1,
      info: {
        data: data,
        title: video.title,
        token: video.token,
        currentTime: playerStore.fulltime,
        formattedTime: formatSeconds(playerStore.fulltime),
        duration: video.duration,
        formattedDuration: formatSeconds(video.duration),
        variables: playbackStore.variables,
        viewer: {
          id: playbackStore.viewer?.id,
          name: playbackStore.viewer?.name,
          email: playbackStore.viewer?.email,
          custom_id: playbackStore.viewer?.custom_id
        },
        currentScore: annStore.currentScore
      }
    }

    const messageObj = JSON.parse(JSON.stringify(obj));

    log.info(1, "🎩 EmitMessage: " + str)
    log.info(1, messageObj)
    playbackStore.messages.unshift(obj)

    // Detect the embedding context to avoid duplicates
    const isInIframe = window !== window.top;
    const isSingleLevelIframe = window.parent === window.top && window !== window.top;
    const isMultiLevelIframe = window.parent !== window.top && window !== window.top;
    const isReactNative = typeof window.ReactNativeWebView !== 'undefined';
    const isIOSWebView = window.webkit && window.webkit.messageHandlers;

    // Check user agent for mobile app WebViews (defensive check)
    const userAgent = navigator.userAgent || '';
    const isPossibleMobileWebView =
      userAgent.includes('Mobile') ||
      userAgent.includes('iPhone') ||
      userAgent.includes('iPad') ||
      userAgent.includes('Android') ||
      // Common WebView indicators
      (!userAgent.includes('Safari') && userAgent.includes('AppleWebKit')) ||
      userAgent.includes('wv') ||
      userAgent.includes('WebView');

    const targetsSent = [];
    const sentTo = new Set(); // Track unique targets to prevent duplicates

    try {
      // STRATEGY: Send to the minimum necessary targets based on context

      // 1. For standard web iframe embeds
      if (isInIframe && !isReactNative && !isIOSWebView && !isPossibleMobileWebView) {
        // For single-level iframe, just send to parent (which is top)
        if (isSingleLevelIframe) {
          try {
            window.parent.postMessage(messageObj, "*");
            targetsSent.push("window.parent (top)");
            sentTo.add(window.parent);
            log.info(2, "🎩 Sent to window.parent (single-level iframe)");
          } catch (e) {
            log.info(2, "🎩 Could not send to window.parent:", e.message);
          }
        }
        // For multi-level iframes, send to both parent and top
        else if (isMultiLevelIframe) {
          try {
            window.parent.postMessage(messageObj, "*");
            targetsSent.push("window.parent");
            sentTo.add(window.parent);
            log.info(2, "🎩 Sent to window.parent");
          } catch (e) {
            log.info(2, "🎩 Could not send to window.parent:", e.message);
          }

          try {
            if (!sentTo.has(window.top)) {
              window.top.postMessage(messageObj, "*");
              targetsSent.push("window.top");
              sentTo.add(window.top);
              log.info(2, "🎩 Sent to window.top");
            }
          } catch (e) {
            log.info(2, "🎩 Could not send to window.top:", e.message);
          }
        }
      }
      // 2. For non-iframe contexts (direct page load or certain WebViews)
      else if (!isInIframe) {
        // Still try to send to parent/top in case we're in a WebView that doesn't report as iframe
        try {
          window.top.postMessage(messageObj, "*");
          targetsSent.push("window.top");
          log.info(2, "🎩 Sent to window.top (non-iframe context)");
        } catch (e) {
          // This is expected to fail in true non-iframe contexts
          log.info(2, "🎩 Not in iframe, could not send to window.top");
        }
      }

      // 3. Special handling for React Native WebView (always add this)
      if (isReactNative) {
        try {
          window.ReactNativeWebView.postMessage(JSON.stringify(messageObj));
          targetsSent.push("ReactNativeWebView");
          log.info(2, "🎩 Sent to ReactNativeWebView");
        } catch (e) {
          log.info(2, "🎩 Could not send to ReactNativeWebView:", e.message);
        }
      }

      // 4. Special handling for iOS WKWebView message handlers
      if (isIOSWebView) {
        try {
          // Try common handler names
          const handlerNames = ['nativeApp', 'ios', 'messageHandler', 'jsHandler', 'nativeInterface', 'app'];
          let sentToHandler = false;

          for (const handlerName of handlerNames) {
            if (window.webkit.messageHandlers[handlerName]) {
              window.webkit.messageHandlers[handlerName].postMessage(messageObj);
              targetsSent.push(`webkit.messageHandlers.${handlerName}`);
              log.info(2, `🎩 Sent to webkit.messageHandlers.${handlerName}`);
              sentToHandler = true;
              break; // Only send to first available handler
            }
          }

          // Also check for any available handler (catch custom names)
          if (!sentToHandler && window.webkit.messageHandlers) {
            const handlers = Object.keys(window.webkit.messageHandlers);
            if (handlers.length > 0) {
              try {
                window.webkit.messageHandlers[handlers[0]].postMessage(messageObj);
                targetsSent.push(`webkit.messageHandlers.${handlers[0]}`);
                log.info(2, `🎩 Sent to webkit.messageHandlers.${handlers[0]} (detected)`);
                sentToHandler = true;
              } catch (e) { }
            }
          }

          // Always also send to window.top for iOS as fallback
          try {
            window.top.postMessage(messageObj, "*");
            targetsSent.push("window.top (iOS)");
            log.info(2, "🎩 Sent to window.top (iOS fallback)");
          } catch (e) {
            log.info(2, "🎩 Could not send to window.top (iOS):", e.message);
          }
        } catch (e) {
          log.info(2, "🎩 Could not send to webkit.messageHandlers:", e.message);
        }
      }

      // 5A. Additional handling for possible mobile WebViews (when not clearly detected)
      if (isPossibleMobileWebView && !isIOSWebView && !isReactNative) {
        // Mobile WebViews might need window.top even if not detected as iOS
        try {
          window.top.postMessage(messageObj, "*");
          targetsSent.push("window.top (mobile WebView)");
          log.info(2, "🎩 Sent to window.top (possible mobile WebView)");
        } catch (e) {
          log.info(2, "🎩 Could not send to window.top (mobile):", e.message);
        }
      }

      // 5B. Handle popup windows
      if (window.opener && window.opener !== window) {
        try {
          window.opener.postMessage(messageObj, "*");
          targetsSent.push("window.opener");
          log.info(2, "🎩 Sent to window.opener");
        } catch (e) {
          log.info(2, "🎩 Could not send to window.opener:", e.message);
        }
      }

      // 6. Fallback: If no messages were sent successfully, try everything
      if (targetsSent.length === 0) {
        log.info(1, "🎩 No targets found, attempting fallback send to all possible targets");

        // Try window.top
        try {
          window.top.postMessage(messageObj, "*");
          targetsSent.push("window.top (fallback)");
        } catch (e) { }

        // Try window.parent
        try {
          if (window.parent !== window) {
            window.parent.postMessage(messageObj, "*");
            targetsSent.push("window.parent (fallback)");
          }
        } catch (e) { }

        // Try window itself (for some WebViews)
        try {
          window.postMessage(messageObj, "*");
          targetsSent.push("window (fallback)");
        } catch (e) { }
      }

      // Log summary
      if (targetsSent.length > 0) {
        log.info(1, `🎩 Message sent to ${targetsSent.length} target(s): ${targetsSent.join(', ')}`);
      } else {
        log.info(1, "🎩 Warning: Message could not be sent to any targets");
      }

    } catch (error) {
      log.error("🎩 Critical error in emitMessage:", error);
      window.top.postMessage(messageObj, "*");
    }
  }

  function receiveMessage(event) {
    // log.info(5,"🎩 ReceiveMessage:")
    // log.info(5,event)
    const playerStore = PlayerStore();
    const playbackStore = PlaybackStore();

    let action = event.data.event

    switch (action) {
      case "request_screenshot":
        log.info(5, "✅ Screenshot")
        playerStore.takeScreenshot().then((dataUrl) => {
          emitMessage("screenshot", dataUrl)
        })
        break
      case "pause_player":
        log.info(1, "✅ Pause")
        playerStore.pause()
        break
      case "enter_fullscreen":
        // Does not work due to security restrictions
        log.info(1, "✅ Enter Fullscreen")
        playerStore.enterFullscreen()
        break
      case "exit_fullscreen":
        log.info(1, "✅ Exit Fullscreen")
        playerStore.exitFullscreen()
        break
      case "play_player":
        log.info(1, "✅ Play")
        playerStore.play()
        playerStore.started = true
        break
      case "rewind":
        log.info(1, "✅ Rewind")
        if (playerStore.playerState.paused) {
          playerStore.seek(playerStore.player.currentTime - 1)
        } else {
          playerStore.seek(playerStore.player.currentTime - 5)
        }
        break
      case "fast_forward":
        log.info(1, "✅ FastForward")
        if (playerStore.playerState.paused) {
          playerStore.seek(playerStore.player.currentTime + 1)
        } else {
          playerStore.seek(playerStore.player.currentTime + 5)
        }
        break
      case "change_time":
        log.info(1, "✅ ChangeTime")
        playerStore.seek(event.data.time || playerStore.currentTime)
        break
      case "set_variable":
        log.info(1, "✅ SetVariable")
        doVariableEvaluation(event.data.variable_name, event.data.variable_value)
        break
      case "godoot":
        log.info(1, "✅ GoDoot")
        playerStore.isDebug = true
        break
      case "closeModal":
        // LATER
        log.info(1, "✅ GoDoot")
        break;
      default:
        break;
    }
  }

  function trackGtagEvent(action, label, category, value) {
    const playbackStore = PlaybackStore();
    const videoStore = VideoStore();

    if (videoStore.video.manage || !videoStore.video.safe_gtag) {
      return
    }
    if (!window || !window.gtag) {
      log.info(2, "🎩 TrackGtagEvent SKIP, no window.gtag")
      return
    }
    log.info(5, "🎩 TrackGtagEvent", action, label, category, value)
    var x = {
      'event_label': label,
      'event_category': category,
      'viewer_id': playbackStore.viewer?.id || ""
    }
    if (value) {
      x['value'] = value
      // this.sayDev("🌕 Track GTAG Event: ", action + ", " + label + ", " + category + ", " + value || '')
    } else {
      // this.sayDev("🌕 Track GTAG Event: ", action + ", " + label + ", " + category)
    }
    x['page_location'] = playbackStore.variables?.ga_page_location || document.referrer || window.location.origin

    log.info(5, window.gtag)
    window.gtag('event', action, x)
  }

  return { emitMessage, receiveMessage, trackGtagEvent }
}